import React from 'react';

/**
 * Badge — small status/label pill. One flat, soft treatment for every tone so badges
 * stop mixing gradient pills with flat pills on the same screen.
 *
 * tone: 'neutral' | 'info' | 'positive' | 'warning' | 'danger'
 * size: 'sm' | 'md'
 * dot:  show a leading status dot in the tone colour.
 */

const TONES = {
    neutral: { chip: 'bg-slate-100 text-slate-700 ring-slate-200', dot: 'bg-slate-400' },
    info: { chip: 'bg-blue-50 text-blue-700 ring-blue-100', dot: 'bg-blue-500' },
    positive: { chip: 'bg-emerald-50 text-emerald-700 ring-emerald-100', dot: 'bg-emerald-500' },
    warning: { chip: 'bg-amber-50 text-amber-800 ring-amber-100', dot: 'bg-amber-500' },
    danger: { chip: 'bg-red-50 text-red-700 ring-red-100', dot: 'bg-red-500' },
};

const SIZES = {
    sm: 'text-[11px] px-2 py-0.5 gap-1',
    md: 'text-xs px-2.5 py-1 gap-1.5',
};

interface BadgeProps {
    tone?: keyof typeof TONES;
    size?: keyof typeof SIZES;
    dot?: boolean;
    className?: string;
    children?: React.ReactNode;
}

const Badge = ({ tone = 'neutral', size = 'md', dot = false, className = '', children }: BadgeProps) => {
    const t = TONES[tone] || TONES.neutral;
    const s = SIZES[size] || SIZES.md;
    return (
        <span
            className={`inline-flex items-center font-semibold rounded-full ring-1 ring-inset ${t.chip} ${s} ${className}`}
        >
            {dot && <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />}
            {children}
        </span>
    );
};

export default Badge;
